"use client"
import { useLocale, useTranslations } from "next-intl";
import AuthLogo from "../auth-logo";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
// NextUI Button removed — using native <button> with spinner everywhere
import { Eye, EyeOff } from "lucide-react"
import { cookies } from "next/headers";
import { useCookies } from "next-client-cookies";
import { trackFunnel } from "@/lib/funnel";
import { fireMetaEvent } from "@/lib/meta/fireEvent";
import { fireGoogleAdsSignupConversion } from "@/lib/googleAds/fireSignupConversion";
import { useSearchParams } from "next/navigation";

// Toggle: when false, skip the 6-digit email OTP step.
// inscription_direct.php creates the account already validated and returns a JWT immediately.
const WITH_VALIDATION_CODE = false

// Décide où rediriger après signup en fonction de l'intent stocké en cookie.
// Cookie posé sur les LP dédiées (ex : /worker/tiktok) — si absent : comportement
// historique (/app). Ne casse aucun flow existant.
function resolveSignupRedirect(): string {
    if (typeof document === "undefined") return "/app"
    const m = document.cookie.match(/(?:^|;\s*)signup_intent=([^;]+)/)
    const intent = m ? decodeURIComponent(m[1]) : ""
    // Consomme le cookie pour ne pas re-jouer la redirection plus tard
    if (intent) {
        document.cookie = "signup_intent=; Max-Age=0; Path=/; SameSite=Lax"
    }
    switch (intent) {
        case "worker_tiktok":
            return "/app/worker/add?type=text_to_video&utm_source=worker_tiktok"
        case "worker_generic":
            // Landing page generique /worker : l'user n'a pas choisi de mode
            // → on l'envoie sur la liste /app/worker pour qu'il sélectionne lui-même
            return "/app/worker?utm_source=worker_generic"
        // Ajouter d'autres intents ici quand on créera /worker/youtube, /worker/clipping, etc.
        default:
            return "/app"
    }
}

export default function SignUpView({ appleLoginUrl }: { appleLoginUrl: string }) {
    const t = useTranslations("Auth")
    const local = useLocale()
    const cookies = useCookies()
    const searchParams = useSearchParams()
    const prefillEmail = searchParams?.get("mail") || ""
    const hideSecondary = searchParams?.get("hideSecondary") === "1"
    const [errorMessage, setErrorMessage] = useState("")
    const [etapeSignup, setEtapeSignup] = useState(0)
    const [email, setEmail] = useState<string>()
    const [isVerifyingCode, setIsVerifyingCode] = useState(false)
    const [isSigningUp, setIsSigningUp] = useState(false)
    const [otp, setOtp] = useState(Array(6).fill(""));
    const [allOtpIsAvailable, setAllOtpIsAvailable] = useState(false)
    // Apple Sign-In : toujours affiche.
    const [showAppleLogin] = useState(true)
    const GoogleSignIn = async () => {
        const { data } = await axios.get('https://vexub.fr/api/vexub_v2/compte/google_geturl.php');
        window.location.href = data.url;
    };

    useEffect(() => {
        console.log(otp)
    }, [otp])

    // Le formulaire email+password est affiche directement (plus de "click pour ouvrir")
    const [hasOpenMailSignupView, setHasOpenMailSignupView] = useState(true)
    const [confirmError, setConfirmError] = useState("")
    const [pwd, setPwd] = useState("")
    const [confirmPwd, setConfirmPwd] = useState("")
    const [showPwd, setShowPwd] = useState(false)
    const [showConfirmPwd, setShowConfirmPwd] = useState(false)

    // Calcule la force du mot de passe et les regles manquantes
    const pwdStrength = useMemo(() => {
        const tips: string[] = []
        if (pwd.length < 8) tips.push("Use at least 8 characters")
        if (!/[a-z]/.test(pwd)) tips.push("Add lowercase letters")
        if (!/[A-Z]/.test(pwd)) tips.push("Add uppercase letters")
        if (!/[0-9]/.test(pwd)) tips.push("Add a number")
        if (!/[^A-Za-z0-9]/.test(pwd)) tips.push("Add a symbol")
        // Score 0-4
        let score = 0
        if (pwd.length >= 8) score++
        if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++
        if (/[0-9]/.test(pwd)) score++
        if (/[^A-Za-z0-9]/.test(pwd)) score++
        let label = ""
        let color = ""
        let bg = ""
        if (pwd.length === 0) { label = ""; color = ""; bg = "" }
        else if (score <= 1) { label = "weak"; color = "text-rose-500"; bg = "bg-rose-500" }
        else if (score === 2) { label = "fair"; color = "text-orange-500"; bg = "bg-orange-500" }
        else if (score === 3) { label = "good"; color = "text-yellow-500"; bg = "bg-yellow-500" }
        else { label = "strong"; color = "text-emerald-500"; bg = "bg-emerald-500" }
        return { score, label, color, bg, tips: tips.slice(0, 3) }
    }, [pwd])

    const inputsRef = useRef<Array<HTMLInputElement>>([]);



    function setCookie(name: any, value: any, days: any) {
        const d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        let expires = "expires=" + d.toUTCString();
        document.cookie = `${name}=${value};${expires};path=/;Secure;SameSite=Strict`;
    }

    const completeCode = (fullCode: string) => {
        console.log("Code complet :", fullCode);
        setIsVerifyingCode(true)
        verifyOTP(fullCode)
    };



    const verifyOTP = async (code: string) => {
        setErrorMessage("")
        const postData = new URLSearchParams();
        postData.append('email', email || "");
        postData.append('secretcode', code);

        try {
            const response = await axios.post('https://vexub.fr/api/vexub_v2/compte/login_2.php', postData);
            console.log(response.data)
            if (response.data.status == "success") {


                let nbComptes = parseInt(localStorage.getItem("nb_comptes") || "0", 10);
                if (nbComptes >= 3) {

                    setErrorMessage(t("TooMuchAccount"))
                    return;
                }

                nbComptes++;
                localStorage.setItem("nb_comptes", `${nbComptes}`);


                setCookie('token', response.data.token, 7);
                setCookie('email', email, 7);

                trackFunnel("signup_complete", { email });
                fireMetaEvent({
                    event_name: "CompleteRegistration",
                    email,
                    external_id: response.data.user_id || email,
                });
                fireGoogleAdsSignupConversion();
                window.location.href = resolveSignupRedirect();
            } else {
                console.log(response);
                console.log("Erreur: Email ou mot de passe incorrect.");
                setErrorMessage(t("ErrorCode"))
                setIsVerifyingCode(false)
            }
        } catch (error) {
            console.error("Erreur lors de la requête : " + error);
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, 6);

        if (pastedData.length === 0) return;

        const newOtp = [...otp];
        for (let i = 0; i < 6; i++) {
            newOtp[i] = pastedData[i] || "";
            if (inputsRef.current[i]) {
                inputsRef.current[i]!.value = pastedData[i] || "";
            }
        }

        setOtp(newOtp);

        // focus sur la dernière case remplie
        const lastIndex = pastedData.length >= 6 ? 5 : pastedData.length;
        inputsRef.current[lastIndex]?.focus();

        if (pastedData.length === 6) {
            completeCode(pastedData);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        setErrorMessage("")
        const value = e.target.value.replace(/\D/g, "");
        if (!value) return;

        const updatedOtp = [...otp];
        updatedOtp[index] = value[0];
        setOtp(updatedOtp);

        if (index < 5) {
            inputsRef.current[index + 1]?.focus();
        }

        if (updatedOtp.every((digit) => digit !== "")) {
            setAllOtpIsAvailable(true)
            completeCode(updatedOtp.join(""));
        } else {
            setAllOtpIsAvailable(false)
        }


    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && e.currentTarget.value && index > 0) {
            e.currentTarget.value = ""
            inputsRef.current[index - 1]?.focus();

        }

        if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
            e.currentTarget.value = ""
            inputsRef.current[index - 1]?.focus();

        }


        const updatedOtp = [...otp];
        updatedOtp[index] = "";
        setOtp(updatedOtp);
        if (updatedOtp.every((digit) => digit !== "")) {
            setAllOtpIsAvailable(true)
        } else {
            setAllOtpIsAvailable(false)
        }

    };

    async function inscription(email: string, password: string, pseudo: string, langSite: string) {
        setErrorMessage("")
        if (password.length < 8) {
            console.error("Le mot de passe doit avoir au moins 8 caractères.");
            setErrorMessage(t("ErrorPasswordSignup"))
            return "Erreur : Le mot de passe doit avoir au moins 8 caractères";
        }



        const affiliationTag = localStorage.getItem('affiliation_tag') || "";
        const firstTimeUrl =
            cookies.get("firsttimeurl") ||
            localStorage.getItem('firsttimeurl') ||
            "RIEN";
        let country = cookies.get("country") || ""
        // const langSite = "fr";

        const postData = new URLSearchParams();
        postData.append('email', email);
        postData.append('mdp', encryptToMD5(password));
        postData.append('pseudo', pseudo);
        postData.append('affiliation_tag', affiliationTag);
        postData.append('langsite', langSite);
        postData.append('firsttimeurl', firstTimeUrl);
        postData.append('country',country || "")



        try {
            const endpoint = WITH_VALIDATION_CODE
                ? 'https://vexub.fr/api/vexub_v2/compte/inscription2.php'
                : 'https://vexub.fr/api/vexub_v2/compte/inscription_direct.php'
            const response = await axios.post(endpoint, postData);
            if (response.data.status == "success") {
                setEmail(email)
                if (WITH_VALIDATION_CODE) {
                    // Original flow — OTP email sent, show 6-digit step
                    setEtapeSignup(1);
                    trackFunnel("signup_start", { email });
                } else {
                    // Direct flow — account already validated, token returned, log in immediately
                    let nbComptes = parseInt(localStorage.getItem("nb_comptes") || "0", 10);
                    if (nbComptes >= 3) {
                        setErrorMessage(t("TooMuchAccount"))
                        return;
                    }
                    nbComptes++;
                    localStorage.setItem("nb_comptes", `${nbComptes}`);
                    setCookie('token', response.data.token, 7);
                    setCookie('email', email, 7);
                    trackFunnel("signup_complete", { email });
                    fireMetaEvent({
                        event_name: "CompleteRegistration",
                        email,
                        external_id: response.data.user_id || email,
                    });
                    fireGoogleAdsSignupConversion();
                    window.location.href = resolveSignupRedirect();
                }
            } else if (response.data.status == "error") {
                if (response.data.msg == "err_compte_existe_deja") {
                    setErrorMessage(t("ErrorEmailAlreadyUsed"))
                } else if (response.data.msg == "err_mailnonvalide") {
                    setErrorMessage(t("ErrorEmailNotValid"))
                } else {
                    setErrorMessage(response.data.msg || "Error")
                }
            } else {
                console.log(response);
            }
        } catch (error) {
            console.error("Erreur lors de la requête : " + error);
        }

    }

    return (
        <div className="relative w-full min-h-screen flex justify-center items-center flex-col px-4 py-6 sm:py-16 bg-transparent">
            {/* Ambient glow (dark only) */}
            <div
                aria-hidden
                className="hidden dark:block pointer-events-none absolute inset-x-0 top-0 h-[480px] opacity-60"
                style={{ background: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(168, 85, 247, 0.18), transparent 70%)" }}
            />
            <div className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#111114] shadow-xl shadow-black/5 dark:shadow-black/40 p-5 sm:p-10">
                {/* Mobile: logo aligné à côté du titre */}
                <div className="flex items-center gap-3 mb-4 sm:hidden [&>div]:mb-0">
                    <AuthLogo />
                    <div className="min-w-0 flex-1">
                        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white leading-tight">
                            Create your account
                        </h1>
                        <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">
                            Enter your details to get started
                        </p>
                    </div>
                </div>

                {/* Desktop: logo centré au-dessus du titre */}
                <div className="hidden sm:flex sm:justify-center sm:mb-8">
                    <AuthLogo />
                </div>
                <h1 className="hidden sm:block sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white text-left mb-2">
                    Create your account
                </h1>
                <p className="hidden sm:block text-sm text-slate-500 dark:text-slate-400 mb-8">
                    Enter your details to create your account
                </p>
                <div className="flex flex-col items-center w-full gap-0 mt-3">
                    <div className="space-y-3 w-full">
                        {!hideSecondary && <><div role="button" tabIndex={0} id='google_login_button' onClick={() => GoogleSignIn()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); GoogleSignIn(); } }} className="cursor-pointer select-none inline-flex items-center justify-center rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background shadow bg-white border border-input hover:bg-accent hover:text-accent-foreground h-10 py-2 px-4 w-full" style={{ background: '#fff', color: '#000', boxShadow: '0 1px 13px 0 rgba(0,0,0,.1),0 1px 2px -1px rgba(0,0,0,.1), 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color)', fontWeight: 600, fontSize: '0.875rem', lineHeight: '1.25rem' }}>
                            <span className="mr-2 w-4">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 186.69 190.5">
                                    <path fill="#4285f4" d="M95.25 77.932v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"></path>
                                    <path fill="#34a853" d="m41.869 113.38-6.972 5.337-24.679 19.223c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"></path>
                                    <path fill="#fbbc05" d="M10.218 52.561C3.724 65.376.001 79.837.001 95.25s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"></path>
                                    <path fill="#ea4335" d="M95.25 37.927c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276C142.442 9.439 120.968 0 95.25 0 58.016 0 25.891 21.388 10.218 52.561L41.91 77.153c7.533-22.514 28.575-39.226 53.34-39.226"></path>
                                </svg>
                            </span>
                            Continue with Google
                        </div>
                        {showAppleLogin && (
                        <button id='google_login_button' onClick={() => document.location.href = appleLoginUrl} className="inline-flex items-center justify-center rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background shadow bg-white border border-input hover:bg-accent hover:text-accent-foreground h-10 py-2 px-4 w-full" style={{ background: '#fff', color: '#000', boxShadow: '0 1px 13px 0 rgba(0,0,0,.1),0 1px 2px -1px rgba(0,0,0,.1), 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color)', fontWeight: 600, fontSize: '0.875rem', lineHeight: '1.25rem' }}>
                            <span className="mr-2 w-4">
                                <svg version="1.1" id="fi_747" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 22.773 22.773" xmlSpace="preserve"><g><g><path d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573
                        c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z" /><path d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334
                        c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0
                        c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019
                        c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464
                        c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648
                        c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z" /></g><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /></g><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /><g /></svg>

                            </span>
                            Continue with Apple
                        </button>
                        )}
                        <div className="flex items-center w-full my-5 sm:my-9" id='divider_google'>
                        <div className="border-t border-slate-200 dark:border-white/10 flex-1" aria-hidden="true" />
                        <div className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-widest px-4">OR</div>
                        <div className="border-t border-slate-200 dark:border-white/10 flex-1" aria-hidden="true" />
                    </div>

                    </>}
                    </div>


                    
                </div>
                {/* EMAIL MDP */}
                <form style={{ "display": hasOpenMailSignupView && etapeSignup == 0 ? "" : "none" }} className="w-full">
                    <div className="space-y-3">
                        {/* Email */}
                        <input
                            id="email"
                            className="w-full h-11 px-3.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0d0d10] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition"
                            type="email"
                            defaultValue={prefillEmail}
                            placeholder="Email"
                            required
                            aria-label="Email"
                        />

                        {/* Password + eye toggle + strength */}
                        <div>
                            <div className="relative">
                                <input
                                    id="password"
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    className="w-full h-11 pl-3.5 pr-10 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0d0d10] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition"
                                    type={showPwd ? "text" : "password"}
                                    placeholder="Password"
                                    autoComplete="new-password"
                                    required
                                    aria-label="Password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    aria-label={showPwd ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
                                >
                                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {pwd && (
                                <div className="mt-2">
                                    <div className="relative h-1 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                                        <div
                                            className={`absolute inset-y-0 left-0 transition-all duration-300 ${pwdStrength.bg}`}
                                            style={{ width: `${(pwdStrength.score / 4) * 100}%` }}
                                        />
                                        {pwdStrength.label && (
                                            <span className={`absolute -top-0.5 right-0 text-[11px] font-semibold ${pwdStrength.color}`} style={{ transform: "translateY(-100%)" }}>
                                                {pwdStrength.label}
                                            </span>
                                        )}
                                    </div>
                                    {pwdStrength.tips.length > 0 && (
                                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500 leading-snug">
                                            {pwdStrength.tips.join(", ")}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Confirm password + eye toggle */}
                        <div>
                            <div className="relative">
                                <input
                                    id="password_confirm"
                                    value={confirmPwd}
                                    onChange={(e) => setConfirmPwd(e.target.value)}
                                    className="w-full h-11 pl-3.5 pr-10 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0d0d10] text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition"
                                    type={showConfirmPwd ? "text" : "password"}
                                    placeholder="Confirm password"
                                    autoComplete="new-password"
                                    required
                                    aria-label="Confirm password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPwd((v) => !v)}
                                    aria-label={showConfirmPwd ? "Hide password" : "Show password"}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
                                >
                                    {showConfirmPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {confirmError && (
                                <p className="text-xs text-rose-500 mt-1.5">{confirmError}</p>
                            )}
                        </div>
                    </div>

                    {/* Terms */}
                    <p className="mt-4 text-xs text-slate-500 dark:text-slate-500 text-center leading-relaxed">
                        By creating an account, you agree to our{" "}
                        <Link href="/cgu" className="underline hover:text-slate-700 dark:hover:text-slate-300">Terms of Service</Link>
                        {" "}and{" "}
                        <Link href="/politique-de-confidentialite" className="underline hover:text-slate-700 dark:hover:text-slate-300">Privacy Policy</Link>.
                    </p>
                    <div className="mt-6">
                        <button
                            type="button"
                            disabled={isSigningUp}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm shadow-lg shadow-purple-900/30 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                            onClick={async (event) => {
                                event.preventDefault()
                                if (isSigningUp) return
                                setConfirmError("")
                                const pseudo = "Vexub_COM"
                                const emailEl = document.getElementById('email') as HTMLInputElement
                                if (pwd !== confirmPwd) {
                                    setConfirmError("Passwords do not match")
                                    return
                                }
                                setIsSigningUp(true)
                                try {
                                    await inscription(emailEl.value, pwd, pseudo, local)
                                } finally {
                                    setIsSigningUp(false)
                                }
                            }}
                        >
                            {isSigningUp && (
                                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                                    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                            )}
                            {isSigningUp ? "Creating account…" : "Create account"}
                        </button>
                        {/* <button className="btn text-sm text-white bg-purple-500 hover:bg-purple-600 w-full shadow-sm group" onClick={(event) => {
                            event.preventDefault();
                            const pseudo = "Vexub_COM";
                            const email = (document.getElementById('email') as HTMLInputElement).value;
                            const password = (document.getElementById('password') as HTMLInputElement).value;
                            inscription(email, password, pseudo, local);
                        }}>
                            {t("signupButton")} <span className="tracking-normal text-purple-300 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1">-&gt;</span>
                        </button> */}

                        <p className='text-red-600 text-center'>{errorMessage}</p>
                    </div>
                </form>

                <div className="flex flex-col gap-3 items-center" style={{ "display": hasOpenMailSignupView && etapeSignup == 1 ? "" : "none" }}>
                    <div className="flex flex-col items-center justify-center">
                        <h2 style={{ fontFamily: "var(--font-inter)", fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>{t("OTPDesc1")}</h2>
                        <p style={{ color: '#cbcbcb', fontSize: '0.9em', marginBottom: '25px', textAlign: 'center' }}>{t("OTPDesc2")}</p>
                    </div>
                    <div className="flex flex-row gap-3" style={{ opacity: isVerifyingCode ? 0.5 : 1, pointerEvents: isVerifyingCode ? "none" : "auto" }}>

                        {[...Array(6)].map((_, i) => (
                            <input
                                key={i}
                                ref={(el) => { inputsRef.current[i] = el! }}
                                inputMode="numeric"
                                type="text"
                                disabled={isVerifyingCode}
                                pattern="[0-9]*"
                                maxLength={1}
                                onPaste={handlePaste}
                                className="w-10 h-10 rounded-xl text-xl text-center text-white"
                                style={{ background: "#27272a" }}
                                onChange={(e) => handleChange(e, i)}
                                onKeyDown={(e) => handleKeyDown(e, i)}
                            />
                        ))}

                    </div>
                    <p className='text-red-600 text-center'>{errorMessage}</p>
                    <button
                        type="button"
                        disabled={!allOtpIsAvailable || isVerifyingCode}
                        onClick={() => { completeCode(otp.join("")) }}
                        className="mt-3 w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm shadow-lg shadow-purple-900/30 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                    >
                        {isVerifyingCode && (
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                        )}
                        {t("Valider")}
                    </button>
                </div>






            </div>

            <div className="text-center mt-4">
                <div className="text-sm text-slate-400">
                    Already have an account? <Link className="font-medium text-purple-500 hover:text-purple-400 transition duration-150 ease-in-out" href="/signin">Sign in</Link>
                </div>
            </div>

        </div>
    )
}

function encryptToMD5(input: string): string {
    const hexDigits = '0123456789abcdef';
    let result = '';

    for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        result += hexDigits.charAt((char >> 4) & 0x0f) + hexDigits.charAt(char & 0x0f);
    }

    return result;
}